import type { ReactNode } from 'react';
import classes from './VesselBadge.module.css';

export interface ShipGlyphProps {
  /** Genişlik (px); yükseklik orantılı (64 × 40). */
  size?: number;
  /** CSS rengi (ör. `var(--felix-navy-900)`); verilmezse içinde durduğu yazının rengi. */
  color?: string;
  className?: string | undefined;
}

/**
 * Gemi çizimi (menünün alt köşesi, gemi kartı yer tutucusu; M6f G1-11). Ham renk yok:
 * `currentColor`. Süsleme: ekran okuyucudan gizli.
 */
export function ShipGlyph({ size = 58, color, className }: ShipGlyphProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 64 40"
      width={size}
      height={Math.round((size * 40) / 64)}
      style={color ? { color } : undefined}
      aria-hidden
    >
      <path d="M6 26h52l-6 10H13z" fill="currentColor" />
      <path
        d="M16 26v-7h30v7M21 19v-6h20v6M27 13V8h9v5"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinejoin="round"
      />
      <path
        d="M22 22.5h3m4 0h3m4 0h3m4 0h1"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
      />
      <path
        d="M2 38c4 0 4-1.6 8-1.6s4 1.6 8 1.6 4-1.6 8-1.6 4 1.6 8 1.6 4-1.6 8-1.6 4 1.6 8 1.6 4-1.6 8-1.6"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        opacity={0.6}
      />
    </svg>
  );
}

export interface VesselBadgeProps {
  /** Gemi uygulamasında gemi adı; web ofiste sloganın ilk satırı. */
  title: string;
  /** Slogan; "·" ya da "|" içeriyorsa iki satıra bölünür (M6f G1-15). */
  subtitle: string;
  /** Sürüm satırı (NR496 [4.1.2]): her zaman rozetin altında. */
  version: ReactNode;
  compact?: boolean;
}

/** Sloganın satırları: "Better Crew · Safer Voyages" → iki satır. */
export function sloganRows(subtitle: string): string[] {
  return subtitle
    .split(/\s*[·|]\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Sol menünün altı (menu-yapisi.md): gemi çizimi, ad ve slogan; altında ürün adı ve sürümü.
 * Slogan kesilmez: en çok iki satır, tam metin `title`'da (personel-sertifikalar.jpeg "Better Crew /
 * Safer Voyages"). Kısa pencerede slogan gizlenir, ad ve sürüm kalır (M6f G1-13). Çizim ham renk
 * kullanmaz: `currentColor`.
 */
export function VesselBadge({ title, subtitle, version, compact = false }: VesselBadgeProps) {
  const rows = sloganRows(subtitle);
  return (
    <div className={classes.root} data-compact={compact || undefined}>
      <div className={classes.badge}>
        <ShipGlyph className={classes.ship} size={compact ? 36 : 58} />
        {compact ? null : (
          <span className={classes.text}>
            <span className={classes.title} title={title}>
              {title}
            </span>
            {rows.length > 0 ? (
              <span className={classes.subtitle} title={subtitle}>
                {rows.length > 1 ? (
                  <>
                    {rows[0]}
                    <br />
                    {rows.slice(1).join(' · ')}
                  </>
                ) : (
                  rows[0]
                )}
              </span>
            ) : null}
          </span>
        )}
      </div>
      <div className={classes.version}>{version}</div>
    </div>
  );
}
